/* eslint-disable no-unused-expressions */
import { IBuildings, ILanguages, ISites } from '@llws/lift-entity-interfaces';
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import {
  CurrentUser,
  TLanguage,
  ISidebarHeader,
  ISelectOption,
  IRsSwal,
  IRsSwalConfig,
  IRouteMetaPermissionConfig,
  TAccessType,
  TDynamicEnv,
  IRouteMetaFooterExtraButton,
} from '@/shared';
import { ICurrentClient } from '@/shared/interfaces/current-client';
import { IGlobalBtnConfig } from '@/shared/interfaces/global-btn-config';
import { IGlobalCheckBoxConfig } from '@/shared/interfaces/global-checkbox-config';
import { getFileLoc } from '@/shared/helpers';
import i18n from '@/i18n';

const widthMapping = {
  small: 480,
  medium: 680,
  large: 880,
  default: 512,
};

export const rootMixin = defineComponent({
  data() {
    return {
      $globalBtnDisabled: false as boolean,
      isBladeReady: false as boolean,
      globalTranslationsConfig: false as boolean,
      globalSaveBtnConfig: {
        disabled: false,
        variant: 'primary',
        text: i18n.t('actions.save') as string,
        iconWeight: '',
        icon: '',
        disableToolTip: false,
        toolTipText: '',
        // eslint-disable-next-line no-console
        handler() { console.warn('global save button clicked. Set behaviour with $rsGlobals.$setGlobalSaveBtnConfig'); },
      } as IGlobalBtnConfig,
      globalDeleteBtnConfig: {
        disabled: false,
        variant: 'delete',
        icon: 'trash-alt',
        iconWeight: '',
        text: i18n.t('actions.delete') as string,
        // eslint-disable-next-line no-console
        handler() { console.warn('Global delete button clicked. Set behaviour with $rsGlobals.$setGlobalDeleteBtnConfig'); },
      } as IGlobalBtnConfig,
      globalTranslationsBtnConfig: {
        disabled: false,
        text: i18n.t('actions.translations') as string,
        handler() { console.warn('Global translations clicked. Set behaviour with $rsGlobals.$setGlobalTranslationsConfig'); },
      } as IGlobalBtnConfig,
      globalCheckBoxConfig: {
        checkBoxValue: false,
        disabled: false,
        text: '',
        trueValue: true,
        falseValue: false,
        // eslint-disable-next-line no-console
        handler() { console.warn('Global checkbox clicked. Set behaviour with $rsGlobals.$setGlobalCheckBoxConfig'); },
      } as IGlobalCheckBoxConfig,
      footerBottomText: '' as string,
      footerCustomText: '' as string,
      footerLastUpdated: '' as string,
      checkBoxChecked: false as Boolean,
    };
  },

  computed: {
    $currentUser(): CurrentUser {
      return this.$store?.getters['users/current'];
    },

    $currentClient(): ICurrentClient {
      return this.$store?.getters['users/current']?.client;
    },

    $corporateSite(): ISites | undefined {
      return this.$currentClient.sites?.find(
        (site) => site.parentClientId === this.$currentClient.id
          && site.sourceClientId === this.$currentClient.id,
      );
    },

    $currentSiteId(): number | null {
      return this.$store.getters.sidebarHeaderData?.data?.siteKey ? this.$store.getters.sidebarHeaderData?.data?.id : null;
    },

    $language(): TLanguage {
      return this.$store?.state?.language || 'en';
    },

    $isTranslateMode(): boolean {
      return this.$store?.state?.language !== 'en';
    },

    $sidebarHeaderData(): ISidebarHeader {
      return this.$store.getters.sidebarHeaderData;
    },

    $siteSourceClientId(): number {
      return this.$store.getters.sidebarHeaderData?.data?.sourceClientId || this.$currentClient?.id;
    },

    $siteUrl(): string {
      return (this.$store.getters.sidebarHeaderData?.data?.siteUrl || '').replace(/(http:\/\/|https:\/\/)/g, '');
    },

    $siteSourceClientKey(): string {
      return this.$store.getters.sidebarHeaderData?.data?.siteKey || this.$currentClient?.clientKey;
    },

    $siteSourceClientLanguages(): ILanguages[] {
      if (this.$store.getters.sidebarHeaderData?.data?.languages) {
        return this.$store.getters.sidebarHeaderData.data.languages;
      }

      if (this.$currentClient?.languages?.length) {
        return this.$currentClient.languages;
      }

      if (this.$currentClient.sites?.find((s) => s.sourceClientId === this.$currentClient.id)) {
        return this.$currentClient.sites?.find((s) => s.sourceClientId === this.$currentClient.id)?.languages ?? [];
      }

      return [];
    },

    $clientLanguageOptions(): ISelectOption[] {
      return this.$convertLanguageToOptions(this.$siteSourceClientLanguages);
    },

    $clientAndSiteLanguages(): ILanguages[] {
      if (this.$store.getters.sidebarHeaderData?.data?.languages) {
        return this.$store.getters.sidebarHeaderData.data.languages;
      }

      const clientAndSiteLanguages: Record<number, ILanguages> = {};
      const { languages, sites, ownedSites } = this.$currentClient;

      languages?.forEach((language) => {
        clientAndSiteLanguages[language.id!] = language;
      });

      [...(sites || []), ...(ownedSites || [])].forEach((site) => {
        site.languages?.forEach((language) => {
          clientAndSiteLanguages[language.id!] = language;
        });
      });

      return Object.values(clientAndSiteLanguages);
    },

    $clientAndSiteLanguageOptions(): ISelectOption[] {
      return this.$convertLanguageToOptions(this.$clientAndSiteLanguages);
    },

    $s3Url(): string {
      return this.$store?.state?.s3Url;
    },

    $isIFrame(): boolean {
      return this.$store?.state?.isIFrame;
    },

    $isClientChild(): boolean {
      return !!(this.$currentClient?.parentId && this.$currentClient?.hasChildPropertiesOnly === 1);
    },

    $propertyAssetClientKey(): string {
      return this.$currentClient.clientKey;
    },

    $propertyDetailsAssetClientKey(): string {
      const currentProperty: IBuildings | undefined = this.$rsGlobals.$sidebarHeaderData?.data as IBuildings | undefined;
      return (currentProperty && this.$isPropertyChild(currentProperty) && this.$currentClient.parent)
        ? this.$currentClient.parent!.clientKey
        : this.$currentClient.clientKey;
    },

    $propertyDetailsClientId(): number | null {
      const currentProperty: IBuildings | undefined = this.$rsGlobals.$sidebarHeaderData?.data as IBuildings | undefined;
      return currentProperty ? currentProperty.clientId : null;
    },

    $isListingScoresDisabled(): boolean {
      return this.$hasClientSettingValue('disable_listing_scores', '1');
    },

    $isAdLayoutsDisabled(): boolean {
      return this.$hasClientSettingValue('disable_ad_layouts', '1');
    },

    $isWebhooksDisabled(): boolean {
      return this.$hasClientSettingValue('disable_webhooks', '1');
    },

    $isAutoRespondersDisabled(): boolean {
      return this.$hasClientSettingValue('disable_auto_responders', '1');
    },

    $isQuickUpdateDisabled(): boolean {
      return this.$hasClientSettingValue('disable_quick_update', '1');
    },

    $isFeatureChatbotEnabled(): boolean {
      return this.$hasClientSettingValue('enable_feature_chatbot', '1');
    },

    $rsSwal(): IRsSwal {
      return {
        success: (rsSwalConfig: IRsSwalConfig) => {
          this.$swal.fire({
            icon: 'success',
            title: rsSwalConfig.title || i18n.t('swal.success') as string,
            text: rsSwalConfig.text || i18n.t('swal.operationSuccessful') as string,
            confirmButtonColor: rsSwalConfig.confirmButtonColor || '#00a889',
            customClass: rsSwalConfig.customClass,
          });
        },
        remove: (rsSwalConfig: IRsSwalConfig) => {
          this.$swal.fire({
            title: rsSwalConfig.title ? `<i class="fas fa-exclamation-triangle rs-text-warning"></i> ${rsSwalConfig.title}` : i18n.t('swal.areYouSure') as string,
            text: rsSwalConfig.text || i18n.t('swal.areYouSureRemove') as string,
            width: widthMapping[rsSwalConfig.width || 'default'],
            html: rsSwalConfig.html,
            showDenyButton: rsSwalConfig.showDenyButton !== undefined ? rsSwalConfig.showDenyButton : !rsSwalConfig.text,
            denyButtonText: rsSwalConfig.denyButtonText || i18n.t('actions.cancel') as string,
            cancelButtonText: rsSwalConfig.cancelButtonText || i18n.t('actions.cancel') as string,
            showCancelButton: rsSwalConfig.showCancelButton !== undefined ? rsSwalConfig.showCancelButton : true,
            showCloseButton: true,
            confirmButtonColor: '#F26175',
            confirmButtonText: rsSwalConfig.confirmButtonText || i18n.t('swal.delete') as string,
            customClass: {
              container: (!rsSwalConfig.text && !rsSwalConfig.html) ? 'swal-remove-container swal-remove-no-text' : 'swal-remove-container',
              htmlContainer: 'swal-remove-body-container',
              title: 'swal-remove-title',
              actions: 'swal-remove-actions',
              closeButton: 'swal-remove-close',
              ...rsSwalConfig.customClass,
            },
          }).then((response) => {
            if (response.isConfirmed && rsSwalConfig.confirmHandler) {
              rsSwalConfig.confirmHandler();
            } else if (rsSwalConfig.rejectHandler) {
              rsSwalConfig.rejectHandler();
            } else if (rsSwalConfig.confirmWithoutSaveHandler) {
              rsSwalConfig.confirmWithoutSaveHandler();
            }
          });
        },
        warning: (rsSwalConfig: IRsSwalConfig) => {
          this.$swal.fire({
            title: rsSwalConfig.title ? `<i class="fas fa-exclamation-triangle rs-icon-warning"></i> ${rsSwalConfig.title}` : 'No Title Inserted',
            text: rsSwalConfig.text,
            showDenyButton: !!((rsSwalConfig.showDenyButton || !rsSwalConfig.text)),
            denyButtonText: rsSwalConfig.denyButtonText || i18n.t('swal.continueWithoutSaving') as string,
            confirmButtonText: rsSwalConfig.confirmButtonText || i18n.t('swal.saveChanges') as string,
            cancelButtonText: rsSwalConfig.cancelButtonText || i18n.t('actions.cancel') as string,
            showCancelButton: rsSwalConfig.showCancelButton,
            showCloseButton: rsSwalConfig.showCloseButton !== undefined ? rsSwalConfig.showCloseButton : true,
            confirmButtonColor: rsSwalConfig.confirmButtonColor || '#00a889',
            allowOutsideClick: rsSwalConfig.allowOutsideClick !== undefined ? rsSwalConfig.allowOutsideClick : true,
            customClass: {
              container: !rsSwalConfig.text ? 'swal-warning-container swal-warning-no-text' : 'swal-warning-container',
              htmlContainer: 'swal-warning-body-container',
              title: 'swal-warning-title',
              actions: 'swal-warning-actions',
              closeButton: rsSwalConfig.showCloseButton === undefined || rsSwalConfig.showCloseButton ? 'swal-warning-close' : '',
              denyButton: rsSwalConfig.denyButtonClass || '',
              ...rsSwalConfig.customClass,
            },
          }).then((response) => {
            if (response.isConfirmed && rsSwalConfig.confirmHandler) {
              rsSwalConfig.confirmHandler();
            } else if (response.isDenied && rsSwalConfig.rejectHandler) {
              rsSwalConfig.rejectHandler();
            } else if (response.isDismissed && rsSwalConfig.dismissHandler) {
              rsSwalConfig.dismissHandler();
            } else if (rsSwalConfig.confirmWithoutSaveHandler) {
              rsSwalConfig.confirmWithoutSaveHandler();
            }
          });
        },
        info: (rsSwalConfig: IRsSwalConfig) => {
          this.$swal.fire({
            title: rsSwalConfig.title ? `<i class="fas fa-info-circle"></i> ${rsSwalConfig.title}` : 'No Title Inserted',
            text: rsSwalConfig.text,
            showDenyButton: !!((rsSwalConfig.showDenyButton || !rsSwalConfig.text)),
            denyButtonText: rsSwalConfig.denyButtonText || i18n.t('swal.continueWithoutSaving') as string,
            confirmButtonText: rsSwalConfig.confirmButtonText || i18n.t('swal.saveChanges') as string,
            showCancelButton: false, // no more cancel button for now
            confirmButtonColor: rsSwalConfig.confirmButtonColor || '#00a889',
            showCloseButton: true,
            customClass: {
              container: !rsSwalConfig.text ? 'swal-warning-container swal-warning-no-text' : 'swal-warning-container',
              htmlContainer: 'swal-warning-body-container',
              title: 'swal-warning-title',
              actions: 'swal-warning-actions',
              closeButton: 'swal-info-close',
              ...rsSwalConfig.customClass,
            },
          }).then((response) => {
            if (response.isConfirmed && rsSwalConfig.confirmHandler) {
              rsSwalConfig.confirmHandler();
            } else if (rsSwalConfig.rejectHandler) {
              rsSwalConfig.rejectHandler();
            } else if (rsSwalConfig.confirmWithoutSaveHandler) {
              rsSwalConfig.confirmWithoutSaveHandler();
            }
          });
        },
      };
    },

    $clientHasRentalsBundle(): boolean {
      return !!this.$rsGlobals.$currentClient.serviceRentsync
        && !!this.$rsGlobals.$currentClient.hasRentalsBundle;
    },
  },

  methods: {
    getFileLoc,

    $featureActive(featureName: string, featureValue: string = '1'): boolean {
      const envs = this.$env as TDynamicEnv;
      return envs[`VUE_APP_USE_${featureName.toUpperCase()}`] === featureValue;
    },

    $setGlobalSaveBtnConfig(config: IGlobalBtnConfig): void {
      this.globalSaveBtnConfig = {
        disabled: false,
        variant: 'primary',
        text: i18n.t('actions.save') as string,
        disableToolTip: true,
        toolTipText: '',
        iconWeight: '',
        icon: '',
        // eslint-disable-next-line no-console
        handler() { console.warn('global save button clicked. Set behaviour with $rsGlobals.$setGlobalSaveBtnConfig'); },
        ...config,
      };
    },

    $setGlobalDeleteBtnConfig(config: IGlobalBtnConfig): void {
      this.globalDeleteBtnConfig = {
        disabled: false,
        icon: 'trash-alt',
        iconWeight: '',
        variant: 'error',
        text: i18n.t('actions.delete') as string,
        // eslint-disable-next-line no-console
        handler() { console.warn('global delete button clicked. Set behaviour with $rsGlobals.$setGlobalDeleteBtnConfig'); },
        ...config,
      };
    },

    $setGlobalTranslationsConfig(config: boolean): void {
      this.globalTranslationsConfig = config;
    },

    $setGlobalTranslationsBtnConfig(config: IGlobalBtnConfig): void {
      this.globalTranslationsBtnConfig = {
        disabled: false,
        text: i18n.t('actions.translations') as string,
        variant: 'secondary',
        // eslint-disable-next-line no-console
        handler() { console.warn('global translations button clicked. Set behaviour with $rsGlobals.$setGlobalDeleteBtnConfig'); },
        ...config,
      };
    },

    $setGlobalCheckBoxConfig(config: IGlobalCheckBoxConfig): void {
      const mergedConfig: IGlobalCheckBoxConfig = {
        checkBoxValue: false,
        disabled: false,
        text: '',
        trueValue: true,
        falseValue: false,
        // eslint-disable-next-line no-console
        handler() { console.warn('global checkbox clicked. Set behaviour with $rsGlobals.$setGlobalCheckBoxConfig'); },
        ...config,
      };

      this.globalCheckBoxConfig = mergedConfig;
    },

    $setFooterExtraButton(config: IRouteMetaFooterExtraButton) {
      this.$eventBus.$emit('set-footer-extra-button', config);
    },

    $onBladeReady(callback: Function): void {
      setTimeout(() => {
        callback();
        this.isBladeReady = true;
      }, 500);
    },

    $userHasAccess(
      permissionKey: string | string[] | IRouteMetaPermissionConfig | IRouteMetaPermissionConfig[],
      accessType?: TAccessType,
      id?: number | string,
    ): boolean {
      if (this.$currentUser) {
        return this.$currentUser.hasPermissionAccess(permissionKey, accessType, id);
      }

      return false;
    },

    $userHasAtLeastOneAccess(
      permissionKey: string | string[] | IRouteMetaPermissionConfig | IRouteMetaPermissionConfig[],
      accessType?: TAccessType,
    ): boolean {
      if (this.$currentUser) {
        return this.$currentUser.hasAtLeastOneModel(permissionKey, accessType ?? 'read');
      }

      return false;
    },

    $hasClientSettingValue(key: string, value: string): boolean {
      if (this.$currentUser) {
        return `${this.$currentUser.getClientSettingValue(key)}`.trim() === `${value}`.trim();
      }

      return false;
    },

    $hasPromotedListingsRestricted(): boolean {
      // Do not restrict Super Users
      if (this.$rsGlobals.$currentUser.isSuperUser) {
        return false;
      }
      const blackListed = this.$rsGlobals.$currentUser.getClientSettingValue('promoted_listings_blacklist') === '1';
      const limitedAccess = (
        !this.$rsGlobals.$userHasAtLeastOneAccess('promoted_listings', 'update')
        || !this.$rsGlobals.$userHasAtLeastOneAccess('standard_listings', 'update')
      );
      // Restrict when current client has the promoted_listings_blacklist feature flag, or does not have permission
      return blackListed || limitedAccess;
    },

    $hasExcludeSyndicationAvailable(buildingId: number): boolean {
      return this.$rsGlobals.$userHasAccess('standard_listings', 'update', buildingId);
    },

    $hasToursRestricted(): boolean {
      if (this.$rsGlobals.$currentUser.isSuperUser) {
        return false;
      }
      return !this.$rsGlobals.$userHasAtLeastOneAccess('tour_admins') && this.$rsGlobals.$userHasAtLeastOneAccess('tour_agents');// Restricted for tour agents
    },

    $isLanguage(languageCode: TLanguage): boolean {
      return (this.$store.state.language || 'en') === languageCode;
    },

    $isPropertyChild(property?: IBuildings): boolean {
      return !!property?.childClientId && property?.childClientId === this.$currentClient.id;
    },

    $setFooterBottomText(text: string) {
      this.$rsGlobals.footerBottomText = text;
    },
    $setFooterCustomText(text: string) {
      this.$rsGlobals.footerCustomText = text;
    },

    $setFooterLastUpdated(date: Date | null) {
      if (dayjs(date).isValid() && date !== null) {
        const isToday = dayjs().isSame(dayjs(date), 'day');
        if (isToday) {
          this.$rsGlobals.footerLastUpdated = i18n.t('dates.today') as string;
        } else {
          const locale = i18n.locale === 'fr' ? 'fr-CA' : 'en-US';
          this.$rsGlobals.footerLastUpdated = date.toLocaleString(
            locale,
            {
              month: 'short', year: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
            },
          );
        }
      } else if (date === null) {
        this.$rsGlobals.footerLastUpdated = '';
      } else {
        console.error('$setFooterLastUpdate excepts argument of type Date only');
      }
    },

    $convertLanguageToOptions(languagesList: ILanguages[]) {
      const languages: ISelectOption[] = languagesList
        .map((language: ILanguages) => ({
          text: language.languageCode!.toUpperCase(),
          value: language.languageCode,
        } as ISelectOption));

      languages.unshift({ text: 'EN', value: 'en' });
      return languages;
    },
  },
});

export type TRootMixin = typeof rootMixin;

export default rootMixin;
